"use client"

import type React from "react"

import { useState, useEffect, useRef } from "react"
import { createClient } from "@/lib/supabase/client"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import {
  Users,
  Calendar,
  Clock,
  MapPin,
  Send,
  Utensils,
  PartyPopper,
  Footprints,
  Bike,
  Car,
  Train,
  HelpCircle,
  Pencil,
  X,
  Check,
  Lock,
  Bell,
  BellOff,
} from "lucide-react"
import { format } from "date-fns"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { PartyDeletionCountdown } from "@/components/party-deletion-countdown"
import { triggerHaptic } from "@/hooks/use-platform"
import { createNotificationWithPush } from "@/lib/utils/push-notifications"

interface PartyDetailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partyId: string | null
}

interface PartyDetails {
  id: string
  title: string
  description: string | null
  party_date: string
  start_time: string | null
  time_of_day: string | null
  is_public: boolean
  has_lunch: boolean
  has_dinner: boolean
  has_party: boolean
  activity_type: string
  organizer_id: string
  organizer_name: string
  organizer_avatar_url: string | null
  spot_name: string
}

interface Participant {
  id: string
  user_id: string
  user_name: string
  user_avatar_url: string | null
  travel_method: string | null
  joined_at: string
}

interface Message {
  id: string
  user_id: string
  user_name: string
  content: string
  created_at: string
}

const travelIcons = {
  walking: Footprints,
  bike: Bike,
  car: Car,
  train: Train,
  need_ride: HelpCircle,
}

const travelLabels = {
  walking: "Walk",
  bike: "Bike",
  car: "Car",
  train: "Train",
  need_ride: "Need Ride",
}

export function PartyDetailModal({ open, onOpenChange, partyId }: PartyDetailModalProps) {
  const [party, setParty] = useState<PartyDetails | null>(null)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState("")
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [currentUserDbId, setCurrentUserDbId] = useState<string | null>(null)
  const [userParticipation, setUserParticipation] = useState<Participant | null>(null)
  const [loading, setLoading] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)

  const [isEditMode, setIsEditMode] = useState(false)
  const [editTitle, setEditTitle] = useState("")
  const [editDescription, setEditDescription] = useState("")
  const [editDate, setEditDate] = useState("")
  const [editTime, setEditTime] = useState("")
  const [editHasLunch, setEditHasLunch] = useState(false)
  const [editHasDinner, setEditHasDinner] = useState(false)
  const [editHasParty, setEditHasParty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  
  // Follow chat state
  const [isFollowingChat, setIsFollowingChat] = useState(false)
  const [loadingFollow, setLoadingFollow] = useState(false)

  useEffect(() => {
    if (open && partyId) {
      fetchCurrentUser()
      fetchPartyDetails()
      fetchParticipants()
      fetchMessages()

      const supabase = createClient()
      const channel = supabase
        .channel(`party-${partyId}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "surf_party_messages",
            filter: `party_id=eq.${partyId}`,
          },
          (payload) => {
            fetchMessages()
          },
        )
        .subscribe()

      return () => {
        supabase.removeChannel(channel)
      }
    }
  }, [open, partyId])

  useEffect(() => {
    if (currentUserDbId && partyId) {
      fetchParticipants()
      checkFollowStatus()
    }
  }, [currentUserDbId, partyId])

  const checkFollowStatus = async () => {
    if (!currentUserDbId || !partyId) return
    
    const supabase = createClient()
    try {
      const { data, error } = await supabase
        .from("surf_party_participants")
        .select("receive_notifications")
        .eq("party_id", partyId)
        .eq("user_id", currentUserDbId)
        .maybeSingle()
      
      if (data) {
        setIsFollowingChat(data.receive_notifications !== false)
      }
    } catch (error) {
      console.error("Error checking follow status:", error)
    }
  }

  const toggleFollowChat = async (checked: boolean) => {
    if (!currentUserDbId || !partyId || !userParticipation) return
    
    triggerHaptic('selection')
    setLoadingFollow(true)
    const supabase = createClient()
    try {
      const { error } = await supabase
        .from("surf_party_participants")
        .update({ receive_notifications: checked })
        .eq("id", userParticipation.id)
      
      if (error) throw error
      setIsFollowingChat(checked)
    } catch (error) {
      console.error("Error toggling follow:", error)
    } finally {
      setLoadingFollow(false)
    }
  }

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        scrollToBottom("smooth")
      }, 100)
    }
  }, [messages])

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    if (viewportRef.current) {
      const scrollHeight = viewportRef.current.scrollHeight
      viewportRef.current.scrollTo({ top: scrollHeight, behavior })
    }
  }

  const fetchCurrentUser = async () => {
    const supabase = createClient()
    const {
      data: { user },
    } = await supabase.auth.getUser()
    setCurrentUserId(user?.id || null)

    if (user?.id) {
      const { data: userData } = await supabase.from("users").select("id").eq("auth_id", user.id).single()

      if (userData) {
        setCurrentUserDbId(userData.id)
      }
    }
  }

  const fetchPartyDetails = async () => {
    if (!partyId) return

    const supabase = createClient()
    setLoading(true)

    try {
      const { data, error } = await supabase
        .from("surf_parties")
        .select(`
          id,
          title,
          description,
          party_date,
          start_time,
          time_of_day,
          is_public,
          has_lunch,
          has_dinner,
          has_party,
          activity_type,
          organizer_id,
          organizer:users!surf_parties_organizer_id_fkey(id, name, first_name, last_name, email, avatar_url),
          spot:surf_spots!surf_parties_surf_spot_id_fkey(name)
        `)
        .eq("id", partyId)
        .single()

      if (error) throw error

      // Get organizer display name: prefer first_name + last_name, fallback to name, then email
      const organizer = data.organizer as any
      const organizerName = 
        (organizer?.first_name && organizer?.last_name)
          ? `${organizer.first_name} ${organizer.last_name}`
          : organizer?.name || organizer?.email?.split("@")[0] || "Unknown"

      setParty({
        id: data.id,
        title: data.title,
        description: data.description,
        party_date: data.party_date,
        start_time: data.start_time,
        time_of_day: data.time_of_day,
        is_public: data.is_public,
        has_lunch: data.has_lunch,
        has_dinner: data.has_dinner,
        has_party: data.has_party,
        activity_type: data.activity_type || "surf",
        organizer_id: data.organizer_id,
        organizer_name: organizerName,
        organizer_avatar_url: organizer?.avatar_url || null,
        spot_name: data.spot?.name || "Unknown Spot",
      })
    } catch (error) {
      console.error("Error fetching party details:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchParticipants = async () => {
    if (!partyId) return

    const supabase = createClient()

    try {
      const { data, error } = await supabase
        .from("surf_party_participants")
        .select(`
          id,
          user_id,
          travel_method,
          joined_at,
          user:users!surf_party_participants_user_id_fkey(name, avatar_url)
        `)
        .eq("party_id", partyId)
        .order("joined_at", { ascending: true })

      if (error) throw error

      const participantsList =
        data?.map((p: any) => ({
          id: p.id,
          user_id: p.user_id,
          user_name: p.user?.name || "Unknown",
          user_avatar_url: p.user?.avatar_url || null,
          travel_method: p.travel_method,
          joined_at: p.joined_at,
        })) || []

      setParticipants(participantsList)

      const userPart = participantsList.find((p) => p.user_id === currentUserDbId)
      setUserParticipation(userPart || null)
    } catch (error) {
      console.error("Error fetching participants:", error)
    }
  }

  const fetchMessages = async () => {
    if (!partyId) return

    const supabase = createClient()

    try {
      const { data, error } = await supabase
        .from("surf_party_messages")
        .select(`
          id,
          user_id,
          content,
          created_at,
          user:users!surf_party_messages_user_id_fkey(name)
        `)
        .eq("party_id", partyId)
        .order("created_at", { ascending: true })

      if (error) throw error

      const messagesList =
        data?.map((m: any) => ({
          id: m.id,
          user_id: m.user_id,
          user_name: m.user?.name || "Unknown",
          content: m.content,
          created_at: m.created_at,
        })) || []

      setMessages(messagesList)
    } catch (error) {
      console.error("Error fetching messages:", error)
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim() || !partyId || !currentUserDbId || !party) return

    const messageContent = newMessage.trim()
    const supabase = createClient()

    try {
      const { data: insertedMsg, error } = await supabase
        .from("surf_party_messages")
        .insert({
          party_id: partyId,
          user_id: currentUserDbId,
          content: messageContent,
        })
        .select("id")
        .single()

      if (error) throw error

      setNewMessage("")
      fetchMessages()

      // Notify other participants
      if (insertedMsg) {
        // Get all participants who want to receive notifications (excluding the sender)
        const { data: recipients } = await supabase
          .from("surf_party_participants")
          .select("user_id")
          .eq("party_id", partyId)
          .eq("receive_notifications", true)
          .neq("user_id", currentUserDbId)

        if (recipients && recipients.length > 0) {
          // Get current user's name
          const { data: senderProfile } = await supabase
            .from("users")
            .select("name")
            .eq("id", currentUserDbId)
            .single()

          const senderName = senderProfile?.name || "Someone"

          for (const recipient of recipients) {
            await createNotificationWithPush(supabase, {
              user_id: recipient.user_id,
              type: "party_chat",
              title: `New message in ${party.title}`,
              message: `${senderName}: ${messageContent.length > 50 ? messageContent.substring(0, 47) + "..." : messageContent}`,
              action_url: `/messages?type=groups&partyId=${partyId}`,
              related_id: insertedMsg.id,
            })
          }
        }
      }
    } catch (error) {
      console.error("Error sending message:", error)
      alert("Failed to send message. Make sure you've joined the party.")
    }
  }

  const handleUpdateTravelMethod = async (travelMethod: string) => {
    if (!partyId || !currentUserId || !userParticipation) return

    const supabase = createClient()

    try {
      const { error } = await supabase
        .from("surf_party_participants")
        .update({ travel_method: travelMethod })
        .eq("id", userParticipation.id)

      if (error) throw error

      fetchParticipants()
    } catch (error) {
      console.error("Error updating travel method:", error)
    }
  }

  const handleEditClick = () => {
    if (!party) return
    setEditTitle(party.title)
    setEditDescription(party.description || "")
    setEditDate(party.party_date)
    setEditTime(party.start_time || "")
    setEditHasLunch(party.has_lunch)
    setEditHasDinner(party.has_dinner)
    setEditHasParty(party.has_party)
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
  }

  const handleSaveEdit = async () => {
    if (!partyId || !party) return
    setIsSaving(true)

    const supabase = createClient()

    try {
      const { error } = await supabase
        .from("surf_parties")
        .update({
          title: editTitle,
          description: editDescription || null,
          party_date: editDate,
          start_time: editTime || null,
          has_lunch: editHasLunch,
          has_dinner: editHasDinner,
          has_party: editHasParty,
        })
        .eq("id", partyId)

      if (error) throw error

      // Refresh party details
      await fetchPartyDetails()
      setIsEditMode(false)
    } catch (error) {
      console.error("Error updating party:", error)
      alert("Failed to update party. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  const getTimeDisplay = () => {
    if (!party) return ""
    if (party.start_time) {
      return party.start_time.slice(0, 5)
    }
    if (party.time_of_day) {
      return party.time_of_day.charAt(0).toUpperCase() + party.time_of_day.slice(1)
    }
    return "All day"
  }

  const isOrganizer = party?.organizer_id === currentUserDbId

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    )
  }

  const formatDateSeparator = (date: Date) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (isSameDay(date, today)) {
      return "Today"
    } else if (isSameDay(date, yesterday)) {
      return "Yesterday"
    } else {
      return format(date, "MMMM d, yyyy")
    }
  }

  if (!party || loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="flex items-center justify-center py-8">
            <Users className="w-8 h-8 animate-pulse text-blue-500" />
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0 bg-white dark:bg-gray-950">
        <div className="flex flex-col h-[85vh]">
          {/* Header */}
          <DialogHeader className="p-6 pb-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl flex items-center gap-2">
                <span>{party.activity_type === "kite" ? "🪁" : "🏄"}</span>
                {isEditMode ? (
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="text-xl font-semibold"
                    placeholder="Party title"
                  />
                ) : (
                  <>
                    {party.title}
                    {!party.is_public && (
                      <Badge variant="secondary" className="flex items-center gap-1 text-xs ml-2">
                        <Lock className="w-3 h-3" />
                        Private
                      </Badge>
                    )}
                  </>
                )}
              </DialogTitle>
              {isOrganizer && (
                <div className="flex gap-2">
                  {isEditMode ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 bg-transparent"
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="gap-2 bg-blue-500 hover:bg-blue-600"
                        onClick={handleSaveEdit}
                        disabled={isSaving}
                      >
                        <Check className="w-4 h-4" />
                        {isSaving ? "Saving..." : "Save"}
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" className="gap-2 bg-transparent" onClick={handleEditClick}>
                      <Pencil className="w-4 h-4" />
                      Edit
                    </Button>
                  )}
                </div>
              )}
            </div>
            {/* Countdown Timer */}
            {!isEditMode && (
              <PartyDeletionCountdown partyDate={party.party_date} startTime={party.start_time} className="mt-3" />
            )}
            {isEditMode ? (
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-date">Date</Label>
                  <Input
                    id="edit-date"
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-time">Time (optional)</Label>
                  <Input id="edit-time" type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    rows={3}
                    placeholder="Add details about your surf party..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Additional Activities</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="edit-lunch"
                        checked={editHasLunch}
                        onCheckedChange={(checked) => setEditHasLunch(checked as boolean)}
                      />
                      <Label htmlFor="edit-lunch" className="font-normal cursor-pointer flex items-center gap-2">
                        <Utensils className="w-4 h-4" />
                        Lunch
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="edit-dinner"
                        checked={editHasDinner}
                        onCheckedChange={(checked) => setEditHasDinner(checked as boolean)}
                      />
                      <Label htmlFor="edit-dinner" className="font-normal cursor-pointer flex items-center gap-2">
                        <Utensils className="w-4 h-4" />
                        Dinner
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="edit-party"
                        checked={editHasParty}
                        onCheckedChange={(checked) => setEditHasParty(checked as boolean)}
                      />
                      <Label htmlFor="edit-party" className="font-normal cursor-pointer flex items-center gap-2">
                        <PartyPopper className="w-4 h-4" />
                        Party afterwards
                      </Label>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {party.spot_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(new Date(party.party_date), "MMM d, yyyy")}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {getTimeDisplay()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Organized by
                    <Avatar className="w-5 h-5 ml-1">
                      <AvatarImage src={party.organizer_avatar_url || undefined} alt={party.organizer_name} />
                      <AvatarFallback className="text-xs">
                        {party.organizer_name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="ml-1">{party.organizer_name}</span>
                  </span>
                  {!party.is_public && (
                    <span className="flex items-center gap-1">
                      <Lock className="w-4 h-4" />
                      Private
                    </span>
                  )}
                </div>
                {party.description && <p className="text-sm text-muted-foreground mt-2">{party.description}</p>}
                <div className="flex gap-2 mt-2">
                  {party.has_lunch && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Utensils className="w-3 h-3" />
                      Lunch
                    </Badge>
                  )}
                  {party.has_dinner && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Utensils className="w-3 h-3" />
                      Dinner
                    </Badge>
                  )}
                  {party.has_party && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <PartyPopper className="w-3 h-3" />
                      Party afterwards
                    </Badge>
                  )}
                </div>
              </>
            )}
          </DialogHeader>

          <Separator />

          {/* Main Content - Now scrollable */}
          <div className="flex-1 overflow-auto">
            <div className="flex flex-col">
              {/* Chat Section - Full Width */}
              <div className="flex flex-col h-[450px] border-b flex-shrink-0 overflow-hidden">
                <div className="p-4 bg-muted/30 flex-shrink-0 flex items-center justify-between">
                  <h3 className="font-semibold text-sm">Chat</h3>
                  
                  {/* Follow chat toggle - shown only when user is part of the party */}
                  {userParticipation && (
                    <div className="flex items-center gap-2">
                      {isFollowingChat ? (
                        <Bell className="w-3.5 h-3.5 text-blue-500" />
                      ) : (
                        <BellOff className="w-3.5 h-3.5 text-muted-foreground" />
                      )}
                      <Label htmlFor="follow-chat" className="text-xs font-medium cursor-pointer">
                        Follow
                      </Label>
                      <Switch
                        id="follow-chat"
                        checked={isFollowingChat}
                        onCheckedChange={toggleFollowChat}
                        disabled={loadingFollow}
                        className="scale-75"
                      />
                    </div>
                  )}
                </div>
                <ScrollArea ref={viewportRef} className="flex-1 p-4 border-x min-h-0">
                  <div className="space-y-4">
                    {messages.length === 0 ? (
                      <p className="text-center text-muted-foreground text-sm py-8">
                        No messages yet. Start the conversation!
                      </p>
                    ) : (
                      messages.map((message, index) => {
                        const messageDate = new Date(message.created_at)
                        const prevMessageDate = index > 0 ? new Date(messages[index - 1].created_at) : null
                        const showDateSeparator = !prevMessageDate || !isSameDay(messageDate, prevMessageDate)

                        return (
                          <div key={message.id}>
                            {showDateSeparator && (
                              <div className="flex items-center justify-center my-4">
                                <div className="bg-muted px-3 py-1 rounded-full">
                                  <span className="text-xs font-medium text-muted-foreground">
                                    {formatDateSeparator(messageDate)}
                                  </span>
                                </div>
                              </div>
                            )}
                            <div
                              className={`flex flex-col ${message.user_id === currentUserDbId ? "items-end" : "items-start"}`}
                            >
                              <div
                                className={`max-w-[80%] rounded-lg px-3 py-2 ${
                                  message.user_id === currentUserDbId ? "bg-blue-500 text-white" : "bg-muted"
                                }`}
                              >
                                <p
                                  className={`text-xs font-semibold mb-1 ${message.user_id === currentUserDbId ? "text-right" : "text-left"}`}
                                >
                                  {message.user_id === currentUserDbId ? "You" : message.user_name}
                                </p>
                                <p
                                  className={`text-sm ${message.user_id === currentUserDbId ? "text-right" : "text-left"}`}
                                >
                                  {message.content}
                                </p>
                              </div>
                              <span className="text-xs text-muted-foreground mt-1">
                                {format(new Date(message.created_at), "HH:mm")}
                              </span>
                            </div>
                          </div>
                        )
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                <div className="p-4 border-t bg-background flex-shrink-0">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={!userParticipation}
                      className="flex-1"
                    />
                    <Button
                      type="submit"
                      size="icon"
                      disabled={!newMessage.trim() || !userParticipation}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                  {!userParticipation && (
                    <p className="text-xs text-muted-foreground mt-2">Join the party to send messages</p>
                  )}
                </div>
              </div>

              {/* Participants Section */}
              <div className="flex flex-col">
                <div className="p-4 bg-muted/30 flex-shrink-0">
                  <h3 className="font-semibold text-sm">Participants ({participants.length})</h3>
                </div>
                <div className="p-4">
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-3 text-sm font-semibold">Name</th>
                          <th className="text-left p-3 text-sm font-semibold">Transport</th>
                          <th className="text-left p-3 text-sm font-semibold">Activities</th>
                        </tr>
                      </thead>
                      <tbody>
                        {participants.map((participant) => {
                          const TravelIcon = participant.travel_method
                            ? travelIcons[participant.travel_method as keyof typeof travelIcons]
                            : null
                          const isCurrentUser = participant.user_id === currentUserDbId

                          return (
                            <tr key={participant.id} className="border-t hover:bg-muted/30">
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <Avatar className="w-8 h-8">
                                    <AvatarImage
                                      src={participant.user_avatar_url || undefined}
                                      alt={participant.user_name}
                                    />
                                    <AvatarFallback className="text-xs">
                                      {participant.user_name.charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <p className="text-sm font-medium">
                                    {participant.user_name}
                                    {isCurrentUser && " (You)"}
                                  </p>
                                </div>
                              </td>
                              <td className="p-3">
                                {isCurrentUser ? (
                                  <Select
                                    value={participant.travel_method || ""}
                                    onValueChange={handleUpdateTravelMethod}
                                  >
                                    <SelectTrigger className="w-[140px] h-9">
                                      <SelectValue placeholder="Select">
                                        {TravelIcon && (
                                          <div className="flex items-center gap-2">
                                            <TravelIcon className="w-4 h-4" />
                                            <span className="text-sm">
                                              {participant.travel_method
                                                ? travelLabels[participant.travel_method as keyof typeof travelLabels]
                                                : "Select"}
                                            </span>
                                          </div>
                                        )}
                                      </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                                      <SelectItem value="walking">
                                        <div className="flex items-center gap-2">
                                          <Footprints className="w-4 h-4" />
                                          Walk
                                        </div>
                                      </SelectItem>
                                      <SelectItem value="bike">
                                        <div className="flex items-center gap-2">
                                          <Bike className="w-4 h-4" />
                                          Bike
                                        </div>
                                      </SelectItem>
                                      <SelectItem value="car">
                                        <div className="flex items-center gap-2">
                                          <Car className="w-4 h-4" />
                                          Car
                                        </div>
                                      </SelectItem>
                                      <SelectItem value="train">
                                        <div className="flex items-center gap-2">
                                          <Train className="w-4 h-4" />
                                          Train
                                        </div>
                                      </SelectItem>
                                      <SelectItem value="need_ride">
                                        <div className="flex items-center gap-2">
                                          <HelpCircle className="w-4 h-4" />
                                          Need Ride
                                        </div>
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : participant.travel_method ? (
                                  <div className="flex items-center gap-2 text-sm">
                                    {TravelIcon && <TravelIcon className="w-4 h-4" />}
                                    {travelLabels[participant.travel_method as keyof typeof travelLabels]}
                                  </div>
                                ) : (
                                  <span className="text-sm text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="p-3">
                                <div className="flex gap-1">
                                  {party.has_lunch && (
                                    <Badge variant="outline" className="text-xs">
                                      🍴 Lunch
                                    </Badge>
                                  )}
                                  {party.has_dinner && (
                                    <Badge variant="outline" className="text-xs">
                                      🍴 Dinner
                                    </Badge>
                                  )}
                                  {party.has_party && (
                                    <Badge variant="outline" className="text-xs">
                                      🎉 Party
                                    </Badge>
                                  )}
                                  {!party.has_lunch && !party.has_dinner && !party.has_party && (
                                    <span className="text-sm text-muted-foreground">-</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
